import {
  DraftRevenueItem,
  hasDraftNoteChange
} from "@/pages/contracts/components/termsDraft/types";
import { DRAFT_CONTRACT_ID } from "@/pages/contracts/components/termsDraft/types";
import {
  toApiDate,
  toApiDateOrNull
} from "@/pages/contracts/components/termsDraft/apiDates";
import { ContractRevenueItem } from "@/pages/contracts/types";
import { TermsPreviewItem } from "./types";

// The set as the API takes it: no id on a new line, deleted lines omitted, total left to the server.
// Notes are sent only while service notes are switched on; otherwise the key is left out.
// With a baseline, an existing line sends its note only when the draft changed it, so a
// save never overwrites a note someone else edited after this draft was opened.
const shouldSendNote = (
  item: DraftRevenueItem,
  includeNote: boolean,
  baseline?: Record<string, ContractRevenueItem>
): boolean => {
  if (!includeNote) return false;
  if (!baseline || !baseline[item.id]) return true;
  return hasDraftNoteChange(item, baseline);
};

export const toPreviewItems = (
  items: DraftRevenueItem[],
  includeNote = false,
  baseline?: Record<string, ContractRevenueItem>
): TermsPreviewItem[] =>
  items
    .filter((item) => !item.draftDeleted)
    .map((item) => ({
      ...(item.contractId === DRAFT_CONTRACT_ID ? {} : { id: item.id }),
      serviceItemId: item.serviceItemId,
      serviceItemName: item.serviceItemName,
      serviceItemPricingId: item.serviceItemPricingId,
      serviceStartDate: toApiDateOrNull(item.serviceStartDate),
      serviceEndDate: toApiDateOrNull(item.serviceEndDate),
      // A stored null goes back as null; defaulting it reads as an edit to the server's diff.
      autoRenewal: item.autoRenewal ?? null,
      quantity: item.quantity,
      pricePerUnit: item.pricePerUnit,
      isDiscountPercentage: item.isDiscountPercentage ?? null,
      discountAmount: item.discountAmount ?? null,
      totalLocal: item.totalLocal,
      billingStartDate: toApiDateOrNull(item.billingStartDate),
      billingEndDate: toApiDateOrNull(item.billingEndDate),
      customBillingFrequency: item.customBillingFrequency,
      customServiceDuration: item.customServiceDuration,
      customOptOutDate: toApiDateOrNull(item.customOptOutDate),
      eqPaymentsNumOfPayments: item.eqPaymentsNumOfPayments,
      eqPaymentsBillingFrequency: item.eqPaymentsBillingFrequency,
      // The save replaces the whole set, so a soft-deleted row sent here comes back alive.
      scheduledPayments: item.scheduledPayments
        .filter((payment) => !payment.isDeleted)
        .map((payment) => ({
          paymentNumber: payment.paymentNumber,
          billingDate: toApiDate(payment.billingDate),
          amount: payment.amount ?? 0,
          percentage: payment.percentage ?? 0
        })),
      ...(shouldSendNote(item, includeNote, baseline)
        ? { note: item.note ?? null }
        : {})
    }));
